import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Camera, ScanLine, CheckCircle, AlertTriangle, X, Sparkles,
  RefreshCw, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";
import { api } from "../api/client";
import { Button, Field } from "../components/UIKit";
import { pickImage, processImageFile } from "../lib/imagePicker";
import { mapNetworkError, RULES_LABELS, INTERVENTION_TYPES } from "../lib/fleet";
import CameraCaptureSheet from "../components/CameraCaptureSheet";

const CATEGORY_OPTIONS = [
  { value: "entretien", label: "Entretien" },
  { value: "reparation", label: "Réparation" },
  { value: "pneus", label: "Pneus" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "document", label: "Document" },
  { value: "autre", label: "Autre" },
];

/**
 * ScanInvoiceReview — Phase 5 workflow entry point.
 *
 * Route: /scan/new?vehicle_id=<optional>
 *
 * States (in order):
 *   picker  → the user picks a photo
 *   analyzing → POST /scan-invoice is in flight (spinner, cancellable)
 *   review  → show the parsed_data and let the user edit every field
 *   confirming → POST /scan-invoice/{id}/confirm is in flight
 *   done    → navigate back to the vehicle detail
 *   error   → show retry
 */
export default function ScanInvoiceReview() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vehicleIdParam = params.get("vehicle_id");

  const [step, setStep] = useState("picker");
  const [photo, setPhoto] = useState(null);
  const [scan, setScan] = useState(null); // {scan_id, parsed_data, warnings, duplicate_of}
  const [vehicles, setVehicles] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  // Editable review fields (initialised from scan.parsed_data)
  const [form, setForm] = useState({
    vehicle_id: vehicleIdParam || "",
    type: "vidange",
    date: "",
    mileage: "",
    amount: "",
    parts_amount: "",
    labor_amount: "",
    tax_amount: "",
    category: "",
    garage: "",
    notes: "",
    line_items: [],
    financeOpen: false,
    linesOpen: true,
  });

  // Idempotence guard against double clicks
  const confirmInFlightRef = useRef(false);

  // Fetch vehicles for the picker (needs at least one available)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vehicles");
        setVehicles(data || []);
      } catch { /* ignore, user can still enter manually */ }
    })();
  }, []);

  const applyParsedToForm = useCallback((parsed, vehicleIdHint) => {
    // Try to match the plate from parsed_data with a user's vehicle.
    let matchedVid = vehicleIdHint || "";
    const plate = parsed?.vehicle_plate?.value;
    if (!matchedVid && plate) {
      const norm = plate.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const hit = vehicles.find((v) => (v.plate || "").toUpperCase().replace(/[^A-Z0-9]/g, "") === norm);
      if (hit) matchedVid = hit.id;
    }
    setForm((prev) => ({
      ...prev,
      vehicle_id: matchedVid || prev.vehicle_id,
      type: parsed?.suggested_type && INTERVENTION_TYPES.includes(parsed.suggested_type) ? parsed.suggested_type : "vidange",
      date: parsed?.invoice_date?.value || "",
      mileage: parsed?.mileage?.value != null ? String(parsed.mileage.value) : "",
      amount: parsed?.total_ttc?.value != null ? String(parsed.total_ttc.value) : "",
      parts_amount: parsed?.parts_amount?.value != null ? String(parsed.parts_amount.value) : "",
      labor_amount: parsed?.labor_amount?.value != null ? String(parsed.labor_amount.value) : "",
      tax_amount: parsed?.tax_amount?.value != null ? String(parsed.tax_amount.value) : "",
      category: parsed?.suggested_category || "",
      garage: parsed?.supplier?.value || "",
      notes: parsed?.notes || "",
      line_items: (parsed?.line_items || []).map((li) => ({ ...li })),
    }));
  }, [vehicles]);

  const startAnalysis = useCallback(async (base64) => {
    setErrorMsg(null);
    setStep("analyzing");
    try {
      const payload = { image_base64: base64 };
      if (vehicleIdParam) payload.vehicle_id = vehicleIdParam;
      const { data } = await api.post("/scan-invoice", payload, { timeout: 30000 });
      setScan(data);
      applyParsedToForm(data.parsed_data, vehicleIdParam);
      setStep("review");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setErrorMsg("Trop d'analyses simultanées ou limite horaire atteinte. Réessayez dans quelques minutes.");
      } else if (status === 413) {
        setErrorMsg("Fichier trop lourd (maximum 8 Mo).");
      } else if (status === 504) {
        setErrorMsg("Analyse trop longue. Retentez avec une photo plus nette.");
      } else {
        setErrorMsg(mapNetworkError(e));
      }
      setStep("error");
    }
  }, [vehicleIdParam, applyParsedToForm]);

  const onPickPhoto = async (fromCamera = false) => {
    // Use the shared picker (handles camera/gallery + resize)
    const img = await pickImage({ fromCamera });
    if (!img) return;
    setPhoto(img);
    // Strip data URL prefix — backend accepts both but we're explicit.
    const b64 = img.startsWith("data:") ? img.split(",", 1 + 1)[1] : img;
    await startAnalysis(b64);
  };

  // Refs for the two dedicated <input type="file"> elements rendered inside
  // the picker step. The "camera" input carries capture="environment" (opens
  // the rear camera on Android/iOS), the "gallery" input has no capture attr
  // (opens the OS photo/file picker).
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // In-app camera sheet (getUserMedia) — primary path for "Prendre une photo".
  // Bypasses the OS file picker entirely (Android's Google Photos was hijacking
  // the <input capture> hint on some devices).
  const [cameraOpen, setCameraOpen] = useState(false);
  const analyseDataUrl = async (img) => {
    if (!img) return;
    setPhoto(img);
    const b64 = img.startsWith("data:") ? img.split(",", 2)[1] : img;
    await startAnalysis(b64);
  };
  const onFilePicked = async (e) => {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = ""; // allow re-picking the same file next time
    if (!file) return;
    let img = null;
    try {
      img = await processImageFile(file);
    } catch {
      img = null;
    }
    if (!img) {
      toast.error("Impossible de lire l'image sélectionnée.");
      return;
    }
    await analyseDataUrl(img);
  };

  const onRetry = () => {
    if (photo) {
      const b64 = photo.startsWith("data:") ? photo.split(",", 2)[1] : photo;
      startAnalysis(b64);
    } else {
      setStep("picker");
    }
  };

  const onRestart = async () => {
    if (scan?.scan_id) {
      try { await api.delete(`/scan-invoice/${scan.scan_id}`); } catch { /* ignore */ }
    }
    setScan(null); setPhoto(null); setStep("picker"); setErrorMsg(null);
    setForm({
      vehicle_id: vehicleIdParam || "", type: "vidange", date: "", mileage: "",
      amount: "", parts_amount: "", labor_amount: "", tax_amount: "", category: "",
      garage: "", notes: "", line_items: [], financeOpen: false, linesOpen: true,
    });
  };

  const onConfirm = async () => {
    if (confirmInFlightRef.current || step === "confirming") return;
    if (!scan?.scan_id) return;
    if (!form.vehicle_id) {
      toast.error("Sélectionnez un véhicule avant de confirmer.");
      return;
    }
    if (!form.date) {
      toast.error("Renseignez la date de la facture.");
      return;
    }
    confirmInFlightRef.current = true;
    setStep("confirming");
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        type: form.type,
        date: form.date,
        mileage: form.mileage ? parseInt(form.mileage, 10) : null,
        amount: form.amount ? parseFloat(form.amount) : null,
        parts_amount: form.parts_amount ? parseFloat(form.parts_amount) : null,
        labor_amount: form.labor_amount ? parseFloat(form.labor_amount) : null,
        tax_amount: form.tax_amount ? parseFloat(form.tax_amount) : null,
        category: form.category || null,
        garage: form.garage || null,
        notes: form.notes || null,
        line_items: form.line_items,
        attach_invoice_photo: true,
      };
      const { data } = await api.post(`/scan-invoice/${scan.scan_id}/confirm`, payload);
      toast.success("Facture enregistrée");
      setStep("done");
      navigate(`/vehicles/${form.vehicle_id}`);
      return data;
    } catch (e) {
      toast.error(mapNetworkError(e) || "Confirmation impossible.");
      setStep("review");
    } finally {
      confirmInFlightRef.current = false;
    }
  };

  const detailSum =
    (parseFloat(form.parts_amount) || 0) +
    (parseFloat(form.labor_amount) || 0) +
    (parseFloat(form.tax_amount) || 0);
  const amountNum = parseFloat(form.amount) || 0;
  const coherenceWarn = detailSum > 0 && amountNum > 0 && Math.abs(detailSum - amountNum) > 0.5
    ? `La somme pièces + main d'œuvre + taxes (${detailSum.toFixed(2)} €) diffère du total (${amountNum.toFixed(2)} €).`
    : null;

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", paddingBottom: 100 }} data-testid="scan-review-page">
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px" }}>
        <button
          onClick={() => (step === "picker" ? navigate(-1) : onRestart())}
          data-testid="scan-back"
          style={{ width: 40, height: 40, borderRadius: 20, background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          <ChevronLeft size={20} color="var(--mf-text)" />
        </button>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--mf-text)" }}>Scanner une facture</h2>
      </div>

      {step === "picker" && (
        <div style={{ padding: "0 20px" }}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFilePicked}
            style={{ display: "none" }}
            data-testid="scan-camera-input"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            onChange={onFilePicked}
            style={{ display: "none" }}
            data-testid="scan-gallery-input"
          />
          <PickerStep
            onPickCamera={() => setCameraOpen(true)}
            onPickGallery={() => galleryInputRef.current?.click()}
          />
          <CameraCaptureSheet
            open={cameraOpen}
            onCancel={() => setCameraOpen(false)}
            onFallbackGallery={() => { setCameraOpen(false); galleryInputRef.current?.click(); }}
            onCapture={async (dataUrl) => {
              setCameraOpen(false);
              await analyseDataUrl(dataUrl);
            }}
          />
        </div>
      )}

      {step === "analyzing" && (
        <AnalyzingStep photo={photo} />
      )}

      {step === "error" && (
        <ErrorStep message={errorMsg} onRetry={onRetry} onRestart={onRestart} photo={photo} />
      )}

      {(step === "review" || step === "confirming") && scan && (
        <ReviewStep
          scan={scan}
          photo={photo}
          form={form}
          setForm={setForm}
          vehicles={vehicles}
          onConfirm={onConfirm}
          onRestart={onRestart}
          coherenceWarn={coherenceWarn}
          isConfirming={step === "confirming"}
        />
      )}
    </div>
  );
}

/* ---------------- Sub-steps ---------------- */

function PickerStep({ onPickCamera, onPickGallery }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="scan-picker">
      <div style={{
        padding: 20, borderRadius: 20,
        background: "linear-gradient(135deg, var(--mf-brand) 0%, #6366f1 100%)",
        color: "#fff", textAlign: "center",
      }}>
        <Sparkles size={32} color="#fff" style={{ marginBottom: 8 }} />
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Analyse intelligente</div>
        <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.5 }}>
          Prenez ou choisissez une photo de votre facture. Nous en extrayons montants, catégorie et prestations. Vous validez avant enregistrement.
        </div>
      </div>
      <button
        onClick={onPickCamera}
        data-testid="scan-pick-camera"
        style={{
          padding: "16px 18px", borderRadius: 16,
          background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
          display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
          boxShadow: "var(--mf-shadow)", textAlign: "left",
        }}
      >
        <span style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(59,130,246,0.15)", color: "#3b82f6", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <Camera size={22} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>Prendre une photo</div>
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)" }}>{"Utilise l'appareil photo (mobile)"}</div>
        </div>
      </button>
      <button
        onClick={onPickGallery}
        data-testid="scan-pick-gallery"
        style={{
          padding: "16px 18px", borderRadius: 16,
          background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
          display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
          boxShadow: "var(--mf-shadow)", textAlign: "left",
        }}
      >
        <span style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(139,92,246,0.15)", color: "#8b5cf6", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <ScanLine size={22} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>Choisir dans la galerie</div>
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)" }}>Photo ou PDF déjà enregistré</div>
        </div>
      </button>
      <div style={{ marginTop: 8, fontSize: 12, color: "var(--mf-text-muted)", textAlign: "center", fontStyle: "italic" }}>
        {"Vos factures ne sont jamais partagées publiquement. Une intervention n'est créée qu'après votre confirmation."}
      </div>
    </div>
  );
}

function AnalyzingStep({ photo }) {
  return (
    <div style={{ padding: "20px 20px 40px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }} data-testid="scan-analyzing">
      {photo && <img src={photo} alt="Facture" style={{ width: "100%", maxWidth: 320, borderRadius: 16, boxShadow: "var(--mf-shadow)", opacity: 0.6 }} />}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 26, height: 26, borderRadius: 13,
          border: "3px solid var(--mf-border)", borderTopColor: "var(--mf-brand)",
          animation: "mf-spin 700ms linear infinite",
        }} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>Analyse en cours…</div>
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>3 à 8 secondes en moyenne</div>
        </div>
      </div>
      <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorStep({ message, onRetry, onRestart, photo }) {
  return (
    <div style={{ padding: 20 }} data-testid="scan-error">
      <div style={{ padding: 18, borderRadius: 14, background: "rgba(239,68,68,0.10)", color: "var(--mf-urgent-text)", display: "flex", gap: 10 }}>
        <AlertTriangle size={20} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>{message || "Analyse impossible."}</div>
      </div>
      {photo && <img src={photo} alt="" style={{ width: "100%", maxWidth: 240, marginTop: 16, borderRadius: 12, opacity: 0.6 }} />}
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <Button variant="secondary" fullWidth onClick={onRestart} testID="scan-error-restart"><RefreshCw size={16} /> Nouvelle photo</Button>
        <Button fullWidth onClick={onRetry} testID="scan-error-retry">Réessayer</Button>
      </div>
    </div>
  );
}

function ReviewStep({ scan, photo, form, setForm, vehicles, onConfirm, onRestart, coherenceWarn, isConfirming }) {
  const parsed = scan.parsed_data || {};
  const warnings = scan.warnings || [];
  const duplicate = scan.duplicate_of;
  const set = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  const setLine = (idx, k, v) => setForm((prev) => {
    const next = [...prev.line_items];
    next[idx] = { ...next[idx], [k]: v };
    return { ...prev, line_items: next };
  });
  const removeLine = (idx) => setForm((prev) => ({
    ...prev, line_items: prev.line_items.filter((_, i) => i !== idx),
  }));

  return (
    <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 14 }} data-testid="scan-review">
      {photo && (
        <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", boxShadow: "var(--mf-shadow)" }}>
          <img src={photo} alt="Facture" style={{ width: "100%", maxHeight: 300, objectFit: "cover" }} data-testid="scan-preview" />
          <button
            onClick={onRestart}
            data-testid="scan-restart"
            style={{
              position: "absolute", top: 10, right: 10,
              padding: "6px 12px", borderRadius: 999,
              background: "rgba(11,25,44,0.55)", color: "#fff", border: "none",
              fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <RefreshCw size={12} /> Refaire
          </button>
        </div>
      )}

      {duplicate && (
        <div data-testid="scan-duplicate-warning" style={{
          padding: 14, borderRadius: 14, background: "rgba(245,158,11,0.10)",
          color: "#b45309", fontSize: 13, fontWeight: 600, display: "flex", gap: 10,
        }}>
          <AlertTriangle size={18} />
          <div>{"Cette facture a déjà été scannée. Vous pouvez confirmer si c'est un rescan volontaire."}</div>
        </div>
      )}

      {warnings.length > 0 && (
        <div data-testid="scan-warnings" style={{
          padding: 12, borderRadius: 12, background: "rgba(245,158,11,0.08)",
          color: "#b45309", fontSize: 12,
        }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Vehicle picker */}
      <div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
          Véhicule
        </label>
        <select
          value={form.vehicle_id}
          onChange={(e) => set("vehicle_id")(e.target.value)}
          data-testid="scan-vehicle"
          style={{
            width: "100%", padding: "12px 14px", borderRadius: 12,
            background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
            color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
          }}
        >
          <option value="">— Sélectionner —</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{v.brand} {v.model} · {(v.plate || "").toUpperCase()}</option>
          ))}
        </select>
        {parsed.vehicle_plate?.value && parsed.vehicle_plate.status !== "not_found" && (
          <div style={{ fontSize: 11, color: "var(--mf-text-muted)", marginTop: 4 }}>
            Immatriculation détectée : <strong>{parsed.vehicle_plate.value}</strong>
          </div>
        )}
      </div>

      <div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
          Type principal
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {INTERVENTION_TYPES.map((t) => {
            const active = t === form.type;
            return (
              <button
                key={t}
                type="button"
                onClick={() => set("type")(t)}
                data-testid={`scan-type-${t}`}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: active ? "var(--mf-brand)" : "var(--mf-bg-muted)",
                  color: active ? "#fff" : "var(--mf-text-secondary)",
                  border: active ? "none" : "1px solid var(--mf-border)", cursor: "pointer",
                }}
              >{RULES_LABELS[t]}</button>
            );
          })}
        </div>
      </div>

      <Field testID="scan-date" label="Date" type="date" value={form.date} onChange={set("date")} />
      <Field testID="scan-garage" label="Garage" value={form.garage} onChange={set("garage")} placeholder="Garage du Parc" />
      <Field testID="scan-mileage" label="Kilométrage" value={form.mileage} onChange={set("mileage")} placeholder="185 000" />
      <Field testID="scan-amount" label="Montant total (€)" value={form.amount} onChange={set("amount")} placeholder="120.50" />

      {/* Details financiers */}
      <div>
        <button
          type="button"
          onClick={() => set("financeOpen")(!form.financeOpen)}
          data-testid="scan-finance-toggle"
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 12px", borderRadius: 10,
            background: "var(--mf-bg-muted)", border: "none", color: "var(--mf-text)",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >
          <span>Détails financiers (optionnel)</span>
          {form.financeOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {form.financeOpen && (
          <div style={{ marginTop: 10 }} data-testid="scan-finance-fields">
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
                Catégorie
              </label>
              <select
                value={form.category}
                onChange={(e) => set("category")(e.target.value)}
                data-testid="scan-category"
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: 12,
                  background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                  color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
                }}
              >
                <option value="">— Auto —</option>
                {CATEGORY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field testID="scan-parts" label="Pièces (€)" value={form.parts_amount} onChange={set("parts_amount")} placeholder="50.00" />
              <Field testID="scan-labor" label="Main-d'œuvre (€)" value={form.labor_amount} onChange={set("labor_amount")} placeholder="70.00" />
            </div>
            <Field testID="scan-tax" label="Taxes / TVA (€)" value={form.tax_amount} onChange={set("tax_amount")} placeholder="24.00" />
            {coherenceWarn && (
              <div data-testid="scan-finance-warn" style={{
                padding: "8px 12px", borderRadius: 10,
                background: "rgba(245,158,11,0.10)", color: "#b45309",
                fontSize: 12, fontWeight: 600,
              }}>⚠ {coherenceWarn}</div>
            )}
          </div>
        )}
      </div>

      {/* Line items */}
      {form.line_items.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => set("linesOpen")(!form.linesOpen)}
            data-testid="scan-lines-toggle"
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 12px", borderRadius: 10,
              background: "var(--mf-bg-muted)", border: "none", color: "var(--mf-text)",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            <span>Prestations détectées ({form.line_items.length})</span>
            {form.linesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {form.linesOpen && (
            <div data-testid="scan-lines" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {form.line_items.map((li, idx) => (
                <div key={idx} style={{
                  padding: 12, borderRadius: 12,
                  background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                }} data-testid={`scan-line-${idx}`}>
                  <input
                    type="text"
                    value={li.description || ""}
                    onChange={(e) => setLine(idx, "description", e.target.value)}
                    style={{
                      width: "100%", padding: "6px 8px", marginBottom: 6,
                      background: "var(--mf-bg-muted)", border: "1px solid var(--mf-border)",
                      borderRadius: 8, fontSize: 13, color: "var(--mf-text)",
                    }}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 6 }}>
                    <NumInput placeholder="Qté" value={li.quantity ?? ""} onChange={(v) => setLine(idx, "quantity", v ? parseFloat(v) : null)} />
                    <NumInput placeholder="P.U." value={li.unit_price ?? ""} onChange={(v) => setLine(idx, "unit_price", v ? parseFloat(v) : null)} />
                    <NumInput placeholder="Total" value={li.total_price ?? ""} onChange={(v) => setLine(idx, "total_price", v ? parseFloat(v) : null)} />
                    <button
                      onClick={() => removeLine(idx)}
                      data-testid={`scan-line-remove-${idx}`}
                      style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: "rgba(239,68,68,0.08)", color: "var(--mf-urgent-solid)",
                        border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Field testID="scan-notes" label="Notes (optionnel)" value={form.notes} onChange={set("notes")} placeholder="…" multiline />

      <Button
        onClick={onConfirm}
        loading={isConfirming}
        disabled={isConfirming || !form.vehicle_id || !form.date}
        testID="scan-confirm"
        icon={<CheckCircle size={18} />}
        style={{ marginTop: 8 }}
      >
        Enregistrer comme intervention
      </Button>
      <div style={{ fontSize: 11, color: "var(--mf-text-muted)", textAlign: "center", fontStyle: "italic" }}>
        {"La facture reste attachée à l'intervention. Aucune ligne financière séparée n'est créée."}
      </div>
    </div>
  );
}

function NumInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "6px 8px",
        background: "var(--mf-bg-muted)", border: "1px solid var(--mf-border)",
        borderRadius: 8, fontSize: 13, color: "var(--mf-text)",
      }}
    />
  );
}
